// Sidebar Navigation Component
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Workflow,
  Plus,
  Settings,
  HelpCircle,
  Download,
  BookOpen,
  Zap,
  ChevronLeft,
  ChevronRight,
  Shield,
  Users,
  BarChart3,
  History,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { Button } from '../ui/button';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Workflows', href: '/workflows', icon: Workflow },
  { name: 'Guía', href: '/guide', icon: BookOpen },
  { name: 'Descargas', href: '/download', icon: Download },
  { name: 'Diagnóstico', href: '/diagnostic', icon: HelpCircle },
  { name: 'Configuración', href: '/settings', icon: Settings },
];

// Item especial CTA — separado para renderizado diferenciado
const ctaItem = { name: 'Crear Workflow', href: '/workflows/new', icon: Plus };

// Navegacion exclusiva para administradores
const adminNavigation = [
  { name: 'Panel Admin', href: '/admin', icon: BarChart3 },
  { name: 'Usuarios', href: '/admin/users', icon: Users },
  { name: 'Todos los Workflows', href: '/admin/workflows', icon: Workflow },
  { name: 'Historial', href: '/admin/history', icon: History },
  { name: 'Configuracion', href: '/admin/settings', icon: Settings },
];

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sidebarExpanded, toggleSidebar } = useUIStore();
  const { userRole } = useAuthStore();
  const isAdmin = userRole === 'admin';

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out dark:bg-gray-900 dark:border-gray-700',
        sidebarExpanded ? 'w-64' : 'w-20'
      )}
    >
      {/* Header */}
      <div className={cn(
        'flex h-16 items-center border-b border-gray-200 transition-all duration-300 dark:border-gray-700',
        sidebarExpanded ? 'px-6 gap-3' : 'px-3 justify-center'
      )}>
        <span className="rounded-xl bg-gradient-to-br from-emerald-500 to-teal-500 p-1.5 inline-flex shadow-sm shadow-emerald-500/20 flex-shrink-0">
          <Zap className="h-5 w-5 text-white" />
        </span>
        {sidebarExpanded && (
          <h1 className="text-xl font-extrabold tracking-tight text-gray-900 dark:text-gray-100 whitespace-nowrap">
            Proceso<span className="text-emerald-600 dark:text-emerald-400">Fácil</span>
          </h1>
        )}
      </div>

      {/* CTA: Crear Workflow */}
      {/* Usa button + navigate en vez de Link para que funcione estando en /workflows/new:
          React Router ignora <Link> al mismo path, pero navigate con state fuerza re-render */}
      <div className="px-3 pt-4 pb-2">
        <button
          onClick={() => {
            if (location.pathname === ctaItem.href) {
              // Ya estamos en /workflows/new — confirmar si hay cambios sin guardar
              const isDirty = (window as unknown as { __workflowEditorDirty?: boolean }).__workflowEditorDirty;
              if (isDirty) {
                const shouldProceed = window.confirm('Tienes cambios sin guardar. ¿Deseas crear un nuevo workflow?');
                if (!shouldProceed) return;
              }
              // Forzar re-render con state único
              navigate(ctaItem.href, { replace: true, state: { reset: Date.now() } });
            } else {
              navigate(ctaItem.href);
            }
          }}
          className={cn(
            'flex items-center rounded-lg text-sm font-semibold transition-all w-full',
            sidebarExpanded ? 'gap-3 px-3 py-2.5' : 'justify-center px-2 py-2.5',
            location.pathname === ctaItem.href
              ? 'bg-emerald-600 text-white shadow-md dark:bg-emerald-500'
              : 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm hover:shadow-md dark:bg-emerald-500 dark:hover:bg-emerald-400'
          )}
          title={!sidebarExpanded ? ctaItem.name : undefined}
        >
          <ctaItem.icon className="h-5 w-5 flex-shrink-0" />
          {sidebarExpanded && (
            <span className="whitespace-nowrap">{ctaItem.name}</span>
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 px-3 py-2">
        {navigation.map((item) => {
          // M-01: Match inteligente — /workflows/:id resalta "Workflows" (no "Nuevo Workflow")
          const isActive = item.href === '/dashboard'
            ? location.pathname === '/dashboard'
            : item.href === '/workflows'
              ? location.pathname === '/workflows' || (location.pathname.startsWith('/workflows/') && location.pathname !== '/workflows/new')
              : location.pathname === item.href;
          const Icon = item.icon;

          return (
            <Link
              key={item.name}
              to={item.href}
              className={cn(
                'flex items-center rounded-lg text-sm font-medium transition-colors group',
                sidebarExpanded ? 'gap-3 px-3 py-2' : 'justify-center px-2 py-2',
                isActive
                  ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                  : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/70 dark:hover:text-gray-100'
              )}
              title={!sidebarExpanded ? item.name : undefined}
            >
              <Icon className="h-5 w-5 flex-shrink-0" />
              {sidebarExpanded && (
                <span className="whitespace-nowrap">{item.name}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Seccion de Administracion (solo visible para admin) */}
      {isAdmin && (
        <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700">
          {sidebarExpanded && (
            <div className="flex items-center gap-2 px-3 py-1.5 mb-1">
              <Shield className="h-3.5 w-3.5 text-amber-500" />
              <span className="text-xs font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                Administración
              </span>
            </div>
          )}
          <nav className="space-y-1">
            {adminNavigation.map((item) => {
              const isActive = item.href === '/admin'
                ? location.pathname === '/admin'
                : location.pathname.startsWith(item.href);
              const Icon = item.icon;

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={cn(
                    'flex items-center rounded-lg text-sm font-medium transition-colors group',
                    sidebarExpanded ? 'gap-3 px-3 py-2' : 'justify-center px-2 py-2',
                    isActive
                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700/70 dark:hover:text-gray-100'
                  )}
                  title={!sidebarExpanded ? item.name : undefined}
                >
                  <Icon className="h-5 w-5 flex-shrink-0" />
                  {sidebarExpanded && (
                    <span className="whitespace-nowrap">{item.name}</span>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      )}

      {/* Toggle Button */}
      <div className="p-3 border-t border-gray-200 dark:border-gray-700">
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="w-full justify-center"
          title={sidebarExpanded ? 'Contraer sidebar' : 'Expandir sidebar'}
        >
          {sidebarExpanded ? (
            <ChevronLeft className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </Button>
      </div>
    </div>
  );
}
